import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/feature/Sidebar';
import OfflineIndicator from '../../components/base/OfflineIndicator';
import { api } from '../../services/api';

interface ProfilMesureClient {
  clientId: string;
  clientNom: string;
  clientTelephone: string;
  mesures: { champId: string; valeur: string }[];
  notes: string;
  derniereMAJ: string;
}

interface ChampMesure {
  id: string;
  nom: string;
  unite: string;
  description?: string;
  obligatoire?: boolean;
}

type ModeVue = 'liste' | 'detail' | 'ajout';

function getUser() {
  const s = localStorage.getItem('atelier_bassoum_user');
  return s ? JSON.parse(s) : { nom: 'Utilisateur', role: 'vendeur' };
}

export default function GestionMesures() {
  const navigate = useNavigate();
  const user = getUser();
  const [mode, setMode] = useState<ModeVue>('liste');
  const [search, setSearch] = useState('');
  const [profilSelectionne, setProfilSelectionne] = useState<ProfilMesureClient | null>(null);
  const [profils, setProfils] = useState<ProfilMesureClient[]>([]);
  const [champs, setChamps] = useState<ChampMesure[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const [editedMesures, setEditedMesures] = useState<{ champId: string; valeur: string }[]>([]);
  const [editedNotes, setEditedNotes] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Nouveaux client
  const [clientSearch, setClientSearch] = useState('');
  const [clientSelectionne, setClientSelectionne] = useState<{ id: string; nom: string; telephone: string } | null>(null);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [newClient, setNewClient] = useState({ nom: '', telephone: '' });
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [champsData, profilsRaw, clientsData] = await Promise.all([
          api.getMesureChamps(token),
          api.getClientProfils(token),
          api.getClients(token)
        ]);
        setChamps(champsData);
        setClients(clientsData);

        // Group profilsRaw by client (it's flat from SQL JOIN)
        const grouped: ProfilMesureClient[] = [];
        (profilsRaw as any[]).forEach(row => {
          let p = grouped.find(g => String(g.clientId) === String(row.clientId));
          if (!p) {
            p = {
              clientId: String(row.clientId),
              clientNom: row.clientNom,
              clientTelephone: row.clientTelephone,
              mesures: [],
              notes: row.notes || '',
              derniereMAJ: new Date(row.updated_at).toLocaleDateString('fr-FR')
            };
            grouped.push(p);
          }
          p.mesures.push({ champId: String(row.champ_mesure_id), valeur: row.valeur });
        });
        setProfils(grouped);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProfils = profils.filter(p =>
    p.clientNom.toLowerCase().includes(search.toLowerCase()) ||
    p.clientTelephone.includes(search)
  );

  const clientsSansMesures = clients.filter(c => !profils.find(p => String(p.clientId) === String(c.id)));
  const clientsFiltres = clientsSansMesures.filter(c =>
    c.nom.toLowerCase().includes(clientSearch.toLowerCase()) || c.telephone.includes(clientSearch)
  );

  const openDetail = (profil: ProfilMesureClient) => {
    setProfilSelectionne(profil);
    setEditedMesures([...profil.mesures]);
    setEditedNotes(profil.notes);
    setMode('detail');
  };

  const saveDetail = async () => {
    if (!profilSelectionne) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.saveClientMesures(token, Number(profilSelectionne.clientId), editedMesures, editedNotes);
      setProfils(prev => prev.map(p =>
        p.clientId === profilSelectionne.clientId
          ? { ...p, mesures: editedMesures, notes: editedNotes, derniereMAJ: new Date().toLocaleDateString('fr-FR') }
          : p
      ));
      setMode('liste');
    } catch (err) {
      console.error(err);
    }
  };

  const saveNewProfil = async () => {
    if (!clientSelectionne) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
        await api.saveClientMesures(token, Number(clientSelectionne.id), editedMesures, editedNotes);
        const newProfil: ProfilMesureClient = {
            clientId: String(clientSelectionne.id),
            clientNom: clientSelectionne.nom,
            clientTelephone: clientSelectionne.telephone,
            mesures: editedMesures,
            notes: editedNotes,
            derniereMAJ: new Date().toLocaleDateString('fr-FR'),
        };
        setProfils(prev => [...prev, newProfil]);
        setMode('liste');
        setClientSelectionne(null);
        setEditedMesures([]);
        setEditedNotes('');
    } catch (err) {
        console.error(err);
    }
  };

  const handleDeleteProfil = async (clientId: string) => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.deleteClientMesures(token, Number(clientId));
      setProfils(prev => prev.filter(p => p.clientId !== clientId));
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuickCreate = async () => {
    if (!newClient.nom || !newClient.telephone) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setIsCreatingClient(true);
    try {
      const res = await api.createClient(token, newClient);
      const createdClient = { id: String(res.id), nom: newClient.nom, telephone: newClient.telephone };
      setClients(prev => [...prev, createdClient]);
      setClientSelectionne(createdClient);
      setShowQuickCreate(false);
      setNewClient({ nom: '', telephone: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setIsCreatingClient(false);
    }
  };

  const getMesureVal = (mesures: { champId: string; valeur: string }[], champId: string) =>
    mesures.find(m => String(m.champId) === String(champId))?.valeur || '';

  const setMesureVal = (champId: string, valeur: string) => {
    setEditedMesures(prev => {
      const existing = prev.find(m => String(m.champId) === String(champId));
      if (existing) return prev.map(m => String(m.champId) === String(champId) ? { ...m, valeur } : m);
      return [...prev, { champId: String(champId), valeur }];
    });
  };

  return (
    <div className="flex h-screen bg-gray-50 font-nunito overflow-hidden">
      <OfflineIndicator />
      <div className="hidden md:block flex-shrink-0">
        <Sidebar role={user.role} nom={user.nom} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {mode !== 'liste' && (
              <button 
                onClick={() => setMode('liste')} 
                title="Retour à la liste"
                aria-label="Retour à la liste"
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line text-gray-600" />
              </button>
            )}
            <div>
              <h1 className="font-playfair font-bold text-lg text-gray-900">
                {mode === 'liste' ? 'Mesures clients' : mode === 'detail' ? profilSelectionne?.clientNom : 'Nouveau profil de mesures'}
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">
                {mode === 'liste' ? `${profils.length} profils enregistrés` : mode === 'detail' ? `Dernière MAJ: ${profilSelectionne?.derniereMAJ}` : 'Sélectionner un client'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'liste' && (
              <>
                <button
                  onClick={() => navigate('/parametres/mesures')}
                  className="flex items-center gap-2 text-xs bg-gray-100 text-gray-600 px-3 py-2 rounded-xl font-semibold cursor-pointer hover:bg-gray-200 whitespace-nowrap"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-settings-3-line" />
                  </div>
                  Configurer champs
                </button>
                <button
                  onClick={() => { setMode('ajout'); setEditedMesures([]); setEditedNotes(''); setClientSelectionne(null); setClientSearch(''); }}
                  className="flex items-center gap-2 text-xs bg-foret-500 text-white px-3 py-2 rounded-xl font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap"
                >
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-add-line" />
                  </div>
                  Ajouter un profil
                </button>
              </>
            )}
            {mode === 'detail' && (
              <button onClick={saveDetail} className="flex items-center gap-2 text-xs bg-foret-500 text-white px-4 py-2 rounded-xl font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-save-line" />
                </div>
                Enregistrer
              </button>
            )}
            {mode === 'ajout' && clientSelectionne && (
              <button onClick={saveNewProfil} className="flex items-center gap-2 text-xs bg-foret-500 text-white px-4 py-2 rounded-xl font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-save-line" />
                </div>
                Enregistrer les mesures
              </button>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
             <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foret-500"></div>
             </div>
          ) : mode === 'liste' ? (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line text-sm" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredProfils.map(profil => {
                  const champsFilled = profil.mesures.filter(m => m.valeur).length;
                  const champsTotal = champs.length || 1;
                  const completude = Math.round((champsFilled / champsTotal) * 100);
                  return (
                    <div
                      key={profil.clientId}
                      className="bg-white rounded-2xl border border-gray-100 p-4 cursor-pointer hover:border-foret-300 hover:bg-foret-50/30 transition-all group relative"
                    >
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(profil.clientId); }}
                        className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer transition-colors z-10 shadow-sm"
                        title="Supprimer le profil"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                      <div onClick={() => openDetail(profil)}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-foret-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {profil.clientNom.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{profil.clientNom}</p>
                            <p className="text-xs text-gray-400">{profil.clientTelephone}</p>
                          </div>
                          <div className="w-5 h-5 flex items-center justify-center text-gray-300 group-hover:text-foret-500 transition-colors">
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                          {champs.slice(0, 4).map(champ => {
                            const val = profil.mesures.find(m => String(m.champId) === String(champ.id))?.valeur;
                            return (
                              <div key={champ.id} className="flex items-center justify-between">
                                <span className="text-xs text-gray-400">{champ.nom}</span>
                                <span className={`text-xs font-semibold ${val ? 'text-gray-800' : 'text-gray-300'}`}>
                                  {val ? `${val} ${champ.unite}` : '—'}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>Complétude</span>
                            <span className="font-semibold text-gray-600">{champsFilled}/{champsTotal} champs</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <style>{`
                              .mesure-compl-bar-${profil.id} {
                                width: ${completude}%;
                              }
                            `}</style>
                            <div
                              className={`h-full rounded-full transition-all ${completude === 100 ? 'bg-foret-400' : completude > 50 ? 'bg-or-400' : 'bg-red-300'} mesure-compl-bar-${profil.id}`}
                            />
                          </div>
                        </div>

                        <p className="text-xs text-gray-400 mt-2">MAJ : {profil.derniereMAJ}</p>
                      </div>
                    </div>
                  );
                })}

                {filteredProfils.length === 0 && (
                  <div className="col-span-3 text-center py-16 text-gray-400">
                    <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-pencil-ruler-2-line text-4xl" />
                    </div>
                    <p className="font-semibold">Aucun profil trouvé</p>
                    <p className="text-sm mt-1">Essayez une autre recherche ou créez un nouveau profil</p>
                  </div>
                )}
              </div>
            </div>
          ) : (mode === 'detail' || (mode === 'ajout' && clientSelectionne)) ? (
            <div className="max-w-2xl mx-auto space-y-6">
              {mode === 'ajout' && clientSelectionne && (
                <div className="bg-foret-50 border border-foret-200 rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-foret-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {clientSelectionne.nom.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-semibold text-foret-800">{clientSelectionne.nom}</p>
                    <p className="text-xs text-foret-600">{clientSelectionne.telephone}</p>
                  </div>
                  <button onClick={() => setClientSelectionne(null)} className="ml-auto text-xs text-foret-600 cursor-pointer hover:text-foret-800 whitespace-nowrap">
                    Changer
                  </button>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="font-playfair font-bold text-base text-gray-900 mb-4">Champs de mesure</h2>
                <div className="bg-or-50 border border-or-200 rounded-xl px-4 py-3 flex items-start gap-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center text-or-600 flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-sm" />
                  </div>
                  <p className="text-xs text-or-700">Toutes les mesures sont en centimètres sauf indication contraire. Prendre les mesures directement sur le corps.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {champs.map(champ => (
                    <div key={champ.id} className="space-y-1">
                      <label className="block text-sm font-semibold text-gray-700">
                        {champ.nom}
                        {champ.obligatoire && <span className="text-red-500 ml-1">*</span>}
                        <span className="text-gray-400 font-normal ml-1">({champ.unite})</span>
                      </label>
                      {champ.description && (
                        <p className="text-xs text-gray-400 italic">{champ.description}</p>
                      )}
                      <input
                        type="number"
                        value={getMesureVal(editedMesures, champ.id)}
                        onChange={e => setMesureVal(champ.id, e.target.value)}
                        placeholder="—"
                        min={0}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <h2 className="font-playfair font-bold text-base text-gray-900 mb-3">Notes & préférences</h2>
                <textarea
                  value={editedNotes}
                  onChange={e => setEditedNotes(e.target.value)}
                  rows={3}
                  placeholder="Ex : préfère les cols ronds, dos légèrement cambré, manches 3/4..."
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 resize-none"
                />
              </div>
            </div>
          ) : mode === 'ajout' && !clientSelectionne ? (
            <div className="max-w-lg mx-auto space-y-4">
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-playfair font-bold text-base text-gray-900">Sélectionner un client</h2>
                  <button 
                    onClick={() => setShowQuickCreate(!showQuickCreate)}
                    className="text-xs font-semibold text-foret-600 hover:text-foret-700 underline cursor-pointer"
                  >
                    {showQuickCreate ? 'Annuler' : '+ Nouveau client'}
                  </button>
                </div>

                {showQuickCreate ? (
                  <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">Nom complet</label>
                      <input
                        type="text"
                        value={newClient.nom}
                        onChange={e => setNewClient(prev => ({ ...prev, nom: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-foret-400 bg-white"
                        placeholder="Ex: Moussa Diarra"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">Téléphone</label>
                      <input
                        type="text"
                        value={newClient.telephone}
                        onChange={e => setNewClient(prev => ({ ...prev, telephone: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-foret-400 bg-white"
                        placeholder="Ex: 70000000"
                      />
                    </div>
                    <button
                      onClick={handleQuickCreate}
                      disabled={!newClient.nom || !newClient.telephone || isCreatingClient}
                      className="w-full py-2 bg-foret-500 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-foret-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isCreatingClient ? 'Création...' : 'Créer et continuer'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative mb-4">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                        <i className="ri-search-line text-sm" />
                      </div>
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={e => setClientSearch(e.target.value)}
                        placeholder="Nom ou téléphone..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50"
                      />
                    </div>
                    {clientsFiltres.length === 0 ? (
                      <div className="text-center py-6">
                        <p className="text-sm text-gray-400 mb-2">Aucun client trouvé.</p>
                        <button 
                          onClick={() => setShowQuickCreate(true)}
                          className="text-xs font-bold text-foret-600 hover:text-foret-700 bg-foret-50 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          Créer "{clientSearch}"
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {clientsFiltres.map(c => (
                          <button
                            key={c.id}
                            onClick={() => setClientSelectionne(c)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-foret-50 hover:border-foret-200 border border-transparent cursor-pointer transition-all text-left"
                          >
                            <div className="w-9 h-9 rounded-full bg-foret-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {c.nom.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-800">{c.nom}</p>
                              <p className="text-xs text-gray-400">{c.telephone}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : null}
        </main>

        {/* Delete Confirmation Modal (Déplacé ici pour être toujours accessible) */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
            <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
              <div className="w-16 h-16 rounded-full bg-red-50 text-red-500 flex items-center justify-center mb-6 mx-auto">
                <i className="ri-delete-bin-line text-3xl" />
              </div>
              <h3 className="font-playfair font-bold text-center text-2xl text-gray-900 mb-3">Supprimer le profil ?</h3>
              <p className="text-sm text-center text-gray-500 mb-8 leading-relaxed">
                Toutes les mesures enregistrées pour ce client seront définitivement effacées. Cette action est irréversible.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 py-4 rounded-2xl border border-gray-100 text-gray-400 font-bold text-sm cursor-pointer hover:bg-gray-50 transition-colors uppercase tracking-widest"
                >
                  Annuler
                </button>
                <button 
                  onClick={() => handleDeleteProfil(showDeleteConfirm)}
                  className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-bold text-sm cursor-pointer hover:bg-red-600 shadow-xl shadow-red-200 transition-all uppercase tracking-widest"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
